import { Node } from '@tiptap/core'
import type { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { Plugin, TextSelection } from '@tiptap/pm/state'
import type { Transaction } from '@tiptap/pm/state'

/** The attribute that marks a section label in the editor's HTML, e.g. `<p data-section-label="…">`. */
export const SECTION_LABEL_ATTRIBUTE = 'data-section-label'

const escapeHtml = (value: string) =>
  value.replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;')

/** The HTML for one section label, as the editor itself writes it. */
export const sectionLabelHtml = (label: string) =>
  `<p ${SECTION_LABEL_ATTRIBUTE}="${escapeHtml(label)}">${escapeHtml(label)}</p>`

const findSectionLabel = (doc: ProseMirrorNode): number | null => {
  let found: number | null = null
  doc.descendants((node, pos) => {
    if (found !== null) {
      return false
    }
    if (node.type.name === 'sectionLabel') {
      found = pos
      return false
    }
    return true
  })
  return found
}

/**
 * Puts `label` back at `pos` (mapped from where it was), on a block boundary, with a paragraph on each
 * side so the caret can reach both sections. The caret stays on the side it was on.
 */
const restoreSectionLabel = (tr: Transaction, pos: number, label: ProseMirrorNode) => {
  const selection = tr.selection
  const { paragraph } = tr.doc.type.schema.nodes
  const $pos = tr.doc.resolve(Math.min(pos, tr.doc.content.size))
  let insertAt = $pos.pos
  if ($pos.parent.isTextblock) {
    if ($pos.parentOffset === 0) {
      insertAt = $pos.before()
    } else if ($pos.parentOffset === $pos.parent.content.size) {
      insertAt = $pos.after()
    } else {
      tr.split($pos.pos)
      insertAt = $pos.pos + 1
    }
  }
  tr.insert(insertAt, label)
  const labelEnd = insertAt + label.nodeSize
  const $after = tr.doc.resolve(labelEnd)
  if (!$after.nodeAfter && paragraph) {
    tr.insert(labelEnd, paragraph.create())
  }
  if (!tr.doc.resolve(insertAt).nodeBefore && paragraph) {
    tr.insert(insertAt, paragraph.create())
  }
  if (selection.empty) {
    // -1 keeps a caret that sat right where the label goes on the side before it.
    tr.setSelection(TextSelection.near(tr.doc.resolve(tr.mapping.map(selection.from, -1))))
  }
}

/**
 * A read-only label line that splits one editor into sections — the Work Order's "Additional Work:"
 * heading between the product description and the additional-work products (ENG-2106). It is an atom,
 * so the text can't be edited, and a plugin puts it back whenever an edit deletes it (Backspace from
 * the line below, a selection across it, select-all + delete). The caller splits the saved HTML on
 * {@link SECTION_LABEL_ATTRIBUTE} to get each section back.
 */
export const SectionLabel = Node.create({
  name: 'sectionLabel',
  group: 'block',
  atom: true,
  selectable: false,
  draggable: false,

  addAttributes() {
    return {
      label: {
        default: '',
        parseHTML: (element) => element.getAttribute(SECTION_LABEL_ATTRIBUTE) ?? element.textContent ?? '',
        renderHTML: () => ({})
      }
    }
  },

  parseHTML() {
    // Above the paragraph rule (50), which would otherwise claim the <p> first.
    return [{ tag: `p[${SECTION_LABEL_ATTRIBUTE}]`, priority: 60 }]
  },

  renderHTML({ node }) {
    return ['p', { [SECTION_LABEL_ATTRIBUTE]: node.attrs.label }, node.attrs.label]
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        appendTransaction: (transactions, oldState, newState) => {
          if (!transactions.some((tr) => tr.docChanged)) {
            return null
          }
          const oldPos = findSectionLabel(oldState.doc)
          if (oldPos === null || findSectionLabel(newState.doc) !== null) {
            return null
          }
          const label = oldState.doc.nodeAt(oldPos)
          if (!label) {
            return null
          }
          const mapped = transactions.reduce((pos, tr) => tr.mapping.map(pos), oldPos)
          const tr = newState.tr
          restoreSectionLabel(tr, mapped, label)
          return tr
        }
      })
    ]
  }
})
